import React, { useState, useEffect, useContext } from 'react';
import './Cart.css';
import Header from './Header';
import { useNavigate } from 'react-router-dom';
import cartem from './images/cartem .png';
import CartContext from './CartContext';
import OrdersContext from './OrdersContext';

function Cart() {
    // Read cartItems directly from CartContext — no separate fetch needed.
    // CartContext already has the data, so this page renders immediately
    // without making its own /api/get_cart_items call.
    const { cartItems, setCartItems, fetchCartCount } = useContext(CartContext);
    const { setOrdersCount } = useContext(OrdersContext);

    const [loading, setLoading] = useState(!cartItems || cartItems.length === 0);
    const [totalPrice, setTotalPrice] = useState(0);
    const [cartFullMessage, setCartFullMessage] = useState('');
    const [showConfirmRemove, setShowConfirmRemove] = useState(false);
    const [itemToRemove, setItemToRemove] = useState(null);
    const navigate = useNavigate();

    const API_URL = process.env.REACT_APP_API_URL || 'https://pyate-backend-production-9749.up.railway.app';

    // Only fetch if CartContext has no data yet (e.g. direct page load)
    useEffect(() => {
        if (cartItems && cartItems.length > 0) {
            setLoading(false);
            return;
        }
        fetchCartCount().then(() => setLoading(false));
    }, []); // runs once on mount only

    useEffect(() => {
        const newTotalPrice = cartItems.reduce(
            (total, item) => total + item.price * item.quantity, 0
        );
        setTotalPrice(newTotalPrice);
        if (cartItems.length > 0) setLoading(false);
    }, [cartItems]);

    const handleCheckout = async () => {
        try {
            const token = localStorage.getItem('token');
            const userId = localStorage.getItem('user_id');
            if (!token || !userId) return;

            const response = await fetch(`${API_URL}/api/place_order`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    userId,
                    cartItems,
                    totalAmount: totalPrice,
                }),
            });

            if (!response.ok) {
                alert('Error placing order. Please try again.');
                return;
            }
            await fetchCartCount();
            setOrdersCount(prev => prev + 1);
            setCartItems([]);
            navigate('/checkout', {
                state: { cartItems, totalPrice, message: 'Your order has been placed successfully!' }
            });
        } catch (error) {
            alert('Error placing order. Please try again.');
        }
    };

    const incrementQuantity = async (itemId) => {
        if (cartItems.length >= 25) {
            setCartFullMessage('Your cart is full. You cannot add more items.');
            return;
        }
        try {
            const token = localStorage.getItem('token');
            if (!token) return;

            const response = await fetch(`${API_URL}/api/cart_items/${itemId}/increment`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            });

            if (!response.ok) {
                if (response.status === 400) {
                    setCartFullMessage('Your cart is full. You cannot add more of this item.');
                }
                return;
            }
            // Update local state immediately — no extra fetch needed
            setCartItems(prev =>
                prev.map(item =>
                    item.item_id === itemId ? { ...item, quantity: item.quantity + 1 } : item
                )
            );
            setCartFullMessage('');
        } catch (error) {
            console.error('Error incrementing quantity:', error);
        }
    };

    const decrementQuantity = async (itemId) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return;

            const response = await fetch(`${API_URL}/api/cart_items/${itemId}/decrement`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            });

            if (!response.ok) return;

            setCartItems(prev =>
                prev.map(item =>
                    item.item_id === itemId
                        ? { ...item, quantity: Math.max(1, item.quantity - 1) }
                        : item
                )
            );
        } catch (error) {
            console.error('Error decrementing quantity:', error);
        }
    };

    const removeItem = async (itemId) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return;

            const response = await fetch(`${API_URL}/api/cart_items/${itemId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            });

            if (!response.ok) return;

            setCartItems(prev => prev.filter(item => item.item_id !== itemId));
        } catch (error) {
            console.error('Error removing item:', error);
        }
    };

    const getDeliveryDate = () => {
        const deliveryDate = new Date();
        deliveryDate.setDate(deliveryDate.getDate() + 3);
        return deliveryDate.toLocaleDateString('en-IN', {
            weekday: 'short', day: 'numeric', month: 'short'
        });
    };

    return (
        <>
            <Header />
            <div className="cart">
                {showConfirmRemove && (
                    <div className="popup-backdrop">
                        <div className="popup-modal">
                            <p>Are you sure you want to remove this item from your cart?</p>
                            <div className="popup-buttons">
                                <button
                                    onClick={async () => {
                                        setShowConfirmRemove(false);
                                        await removeItem(itemToRemove);
                                    }}
                                    className="confirm-btn"
                                >
                                    Yes, Remove
                                </button>
                                <button
                                    onClick={() => {
                                        setShowConfirmRemove(false);
                                        setItemToRemove(null);
                                    }}
                                    className="cancel-btn"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {cartFullMessage && <div className="cart-full-message">{cartFullMessage}</div>}

                {loading ? (
                    <div className="loading-container-cart">
                        <div className="loading-spinner-cart"></div>
                        <p>Loading your cart...</p>
                    </div>
                ) : cartItems.length === 0 ? (
                    <div className="empty-cart">
                        <img src={cartem} alt="Empty Cart" />
                        <h3>Your cart is empty</h3>
                        <p>Add items to it now</p>
                        <button className="empty-cart-button" onClick={() => navigate('/Shop')}>
                            Shop Now
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="cart-header">
                            <h2>My Cart ({cartItems.length})</h2>
                        </div>
                        <div className="cart-content">
                            <div className="cart-items-container">
                                {cartItems.map(item => (
                                    <div className="cart-item-card" key={item.item_id}>
                                        <div className="cart-item-main">
                                            <img
                                                src={item.image_url}
                                                alt={item.product_name}
                                                className="cart-item-image"
                                                loading="lazy"
                                            />
                                            <div className="cart-item-details">
                                                <h3 className="product-name">{item.product_name}</h3>
                                                <div className="seller-info">Seller: RetailNet</div>
                                                <div className="price-section">
                                                    <div className="current-price">₹{item.price.toLocaleString('en-IN')}</div>
                                                </div>
                                                <div className="delivery-info">
                                                    <span className="delivery-icon">🚚</span>
                                                    <span className="delivery-text">
                                                        Delivery by <span className="delivery-date">{getDeliveryDate()}</span>
                                                    </span>
                                                </div>
                                                <div className="item-actions">
                                                    <div className="quantity-controls">
                                                        <button
                                                            className="quantity-btn"
                                                            onClick={() => decrementQuantity(item.item_id)}
                                                            disabled={item.quantity <= 1}
                                                        >−</button>
                                                        <span className="quantity-value">{item.quantity}</span>
                                                        <button
                                                            className="quantity-btn"
                                                            onClick={() => incrementQuantity(item.item_id)}
                                                        >+</button>
                                                    </div>
                                                    <button
                                                        className="remove-button"
                                                        onClick={() => {
                                                            setShowConfirmRemove(true);
                                                            setItemToRemove(item.item_id);
                                                        }}
                                                    >REMOVE</button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="price-summary">
                                <div className="price-summary-header">PRICE DETAILS</div>
                                <div className="price-detail-row">
                                    <span>Price ({cartItems.length} items)</span>
                                    <span>₹{totalPrice.toLocaleString('en-IN')}</span>
                                </div>
                                <div className="price-detail-row total">
                                    <span>Total Amount</span>
                                    <span>₹{totalPrice.toLocaleString('en-IN')}</span>
                                </div>
                                <button className="continue-button" onClick={handleCheckout}>
                                    PLACE ORDER
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </>
    );
}

export default Cart;